// src/components/dashboard/AreaChart.tsx
import React from "react";
import { Box, useTheme, Typography, Paper } from "@mui/material";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";

type Props = {
  options: ApexOptions;
  series: ApexAxisChartSeries;
  height?: number;
  loading?: boolean;
  emptyMessage?: string;
};

const AreaChart: React.FC<Props> = ({
  options,
  series,
  height = 350,
  loading = false,
  emptyMessage = "No data available",
}) => {
  const theme = useTheme();
  const prefersReducedMotion = useReducedMotion();
  const isDark = theme.palette.mode === "dark";

  const hasAnyPoint =
    Array.isArray(series) &&
    series.length > 0 &&
    series.some(
      (s: any) =>
        Array.isArray(s?.data) &&
        s.data.some((n: any) => n !== null && n !== undefined)
    );

  const isEmpty = !loading && !hasAnyPoint;

  const accent =
    (Array.isArray(options?.colors) && (options.colors?.[0] as string)) ||
    (isDark ? "#7dd3fc" : "#2563eb");

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: isDark ? "#1A1C23" : "#ffffff",
          p: 2,
          borderRadius: 3,
          border: `1px solid ${isDark ? "#2A2D38" : "#e5e9ed"}`,
          mb: 2,
        }}
      >
        <AnimatePresence mode="wait">
          {loading ? (
            <SlowEleganceLoader
              key="area-loader"
              height={height}
              accent={accent}
              loadingText="Loading"
            />
          ) : isEmpty ? (
            <motion.div
              key="area-empty"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0, transition: { duration: 0.35 } }}
              exit={{ opacity: 0, y: -6, transition: { duration: 0.25 } }}
              style={{
                minHeight: height,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px dashed",
                borderColor: isDark ? "#334155" : "#cfd6e3",
                borderRadius: 12,
                color: (theme.palette as any).text?.secondary,
              }}
            >
              <Typography variant="body2">{emptyMessage}</Typography>
            </motion.div>
          ) : (
            <motion.div
              key="area-chart"
              initial={
                prefersReducedMotion
                  ? { opacity: 0 }
                  : { opacity: 0, filter: "blur(8px)", clipPath: "inset(0% 100% 0% 0%)" }
              }
              animate={
                prefersReducedMotion
                  ? { opacity: 1, transition: { duration: 0.6 } }
                  : {
                      opacity: 1,
                      filter: "blur(0px)",
                      clipPath: "inset(0% 0% 0% 0%)",
                      transition: {
                        duration: 1.2,
                        ease: [0.2, 0, 0, 1],
                        filter: { duration: 0.8 },
                      },
                    }
              }
              exit={
                prefersReducedMotion
                  ? { opacity: 0, transition: { duration: 0.25 } }
                  : { opacity: 0, clipPath: "inset(0% 100% 0% 0%)", transition: { duration: 0.5, ease: "easeInOut" } }
              }
              whileHover={{ y: prefersReducedMotion ? 0 : -1.5, transition: { duration: 0.2 } }}
              style={{ willChange: "opacity, filter, clip-path, transform" }}
            >
              <Chart options={options} series={series} type="area" height={height} />
            </motion.div>
          )}
        </AnimatePresence>
      </Paper>
    </Box>
  );
};

const SlowEleganceLoader: React.FC<{ height: number; accent: string; loadingText: string }> = ({
  height,
  accent,
  loadingText,
}) => {
  const theme = useTheme();
  const prefersReducedMotion = useReducedMotion();
  const isDark = theme.palette.mode === "dark";

  const gridColor = isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9";
  const glassBg = isDark ? "#1A1C23" : "#ffffff";
  const glassBorder = isDark ? "rgba(148,163,184,0.16)" : "#e5e9ed";
  const line2 = isDark ? "#94a3b8" : "#64748b";

  return (
    <motion.div
      initial={{ opacity: 0.7 }}
      animate={{ opacity: 1, transition: { duration: 0.6 } }}
      exit={{ opacity: 0, transition: { duration: 0.35 } }}
      style={{ minHeight: height, position: "relative", borderRadius: 12, overflow: "hidden" }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: isDark ? `
            linear-gradient(#1f232f, #1f232f),
            repeating-linear-gradient(0deg, transparent 0px, transparent 23px, ${gridColor} 24px),
            repeating-linear-gradient(90deg, transparent 0px, transparent 39px, ${gridColor} 40px)
          ` : "none",
          backgroundColor: isDark ? undefined : "#ffffff",
          backgroundBlendMode: isDark ? "multiply" : undefined,
        }}
      />
      {!prefersReducedMotion && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            "::after": {
              content: '""',
              position: "absolute",
              top: 0,
              bottom: 0,
              left: "-25%",
              width: "22%",
              background: isDark
                ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent)"
                : "linear-gradient(90deg, transparent, rgba(15,23,42,0.03), transparent)",
              filter: "blur(10px)",
              animation: "scanXSlow 4.5s linear infinite",
            },
            "@keyframes scanXSlow": {
              "0%": { transform: "translateX(0)" },
              "100%": { transform: "translateX(450%)" },
            },
          }}
        />
      )}

      <Box
        sx={{
          position: "absolute",
          inset: 12,
          borderRadius: 2.5,
          background: glassBg,
          border: `1px solid ${glassBorder}`,
          boxShadow: isDark
            ? "0 10px 32px rgba(2,8,23,0.35), inset 0 1px 0 rgba(255,255,255,0.04)"
            : "none",
          backdropFilter: "blur(8px)",
          overflow: "hidden",
          display: "grid",
          placeItems: "center",
        }}
      >
        {/* SVG animation */}
        <svg width="92%" height="70%" viewBox="0 0 1000 300" preserveAspectRatio="none">
          <defs>
            <linearGradient id="fillA" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity="0.25" />
              <stop offset="100%" stopColor={accent} stopOpacity="0.05" />
            </linearGradient>
            <linearGradient id="strokeA" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={accent} stopOpacity="0.9" />
              <stop offset="100%" stopColor={accent} stopOpacity="0.45" />
            </linearGradient>
            <filter id="softGlow">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <motion.path
            d="M0,230 C120,200 200,245 320,210 C440,175 540,225 660,185 C800,140 900,210 1000,170 L1000,300 L0,300 Z"
            fill="url(#fillA)"
            initial={false}
            animate={prefersReducedMotion ? {} : { y: [6, -6, 6] }}
            transition={prefersReducedMotion ? {} : { duration: 5.5, ease: "easeInOut", repeat: Infinity }}
          />
          <motion.path
            d="M0,235 C120,205 200,240 320,205 C440,170 540,218 660,180 C800,135 900,205 1000,165 L1000,300 L0,300 Z"
            fill="url(#fillA)"
            opacity={0.8}
            initial={false}
            animate={prefersReducedMotion ? {} : { y: [-5, 5, -5] }}
            transition={prefersReducedMotion ? {} : { duration: 6.2, ease: "easeInOut", repeat: Infinity }}
          />

          <line x1="0" y1="250" x2="1000" y2="250" stroke={line2} strokeWidth="1" opacity="0.6" />

          <motion.path
            d="M0,235 C120,205 200,240 320,205 C440,170 540,218 660,180 C800,135 900,205 1000,165"
            fill="none"
            stroke="url(#strokeA)"
            strokeWidth="3"
            filter="url(#softGlow)"
            strokeLinecap="round"
            initial={{ pathLength: prefersReducedMotion ? 1 : 0.05, opacity: 0.92 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{
              duration: prefersReducedMotion ? 0.6 : 6,
              ease: [0.22, 1, 0.36, 1],
              repeat: prefersReducedMotion ? 0 : Infinity,
              repeatDelay: 0.8,
            }}
          />
        </svg>
        <LoadingDots label={loadingText} />
      </Box>
    </motion.div>
  );
};

const LoadingDots: React.FC<{ label: string }> = ({ label }) => {
  const theme = useTheme();
  const prefersReducedMotion = useReducedMotion();
  const color = theme.palette.text.secondary;

  if (prefersReducedMotion) {
    return (
      <Box
        sx={{
          position: "absolute",
          bottom: 10,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
          {label}...
        </Typography>
      </Box>
    );
  }

  const Dot = ({ delay = 0 }: { delay?: number }) => (
    <motion.span
      initial={{ opacity: 0.3, y: 0 }}
      animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
      transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut", delay }}
      style={{ display: "inline-block", marginLeft: 2 }}
    >
      .
    </motion.span>
  );

  return (
    <Box
      sx={{
        position: "absolute",
        bottom: 10,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        color,
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
        {label}
      </Typography>
      <Typography variant="h5" component="span" sx={{ fontWeight: 700 }}>
        <Dot delay={0} />
        <Dot delay={0.2} />
        <Dot delay={0.4} />
      </Typography>
    </Box>
  );
};

export default AreaChart;
